import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ServiceProviderProfileForm from '../components/Profile/ServiceProviderProfileForm';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components/common/UIComponents';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import ServiceWorkspaceBackLink from '../components/Service/ServiceWorkspaceBackLink';
import {
  getOwnSpecializationProposalOutcome,
  SpecializationProposalOutcome,
} from '../services/specializationProposalService';
import {
  notificationService,
  ServiceVerificationDecisionOutcome,
} from '../services/notificationService';

type OutcomeLoadState = 'idle' | 'loading' | 'success' | 'unavailable';

const EditServiceProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const {
    specializationSuggestionId,
    notificationId,
    verificationDecisionId,
    isOnboardingFlow,
    forceResubmission,
  } = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      specializationSuggestionId: (params.get('specializationSuggestionId') || '').trim(),
      notificationId: (params.get('notificationId') || '').trim(),
      verificationDecisionId: (params.get('verificationDecisionId') || '').trim(),
      isOnboardingFlow: params.get('onboarding') === '1',
      forceResubmission: params.get('resubmit') === '1',
    };
  }, [location.search]);
  const [outcomeLoadState, setOutcomeLoadState] = React.useState<OutcomeLoadState>('idle');
  const [specializationOutcome, setSpecializationOutcome] =
    React.useState<SpecializationProposalOutcome | null>(null);
  const [verificationOutcomeLoadState, setVerificationOutcomeLoadState] =
    React.useState<OutcomeLoadState>('idle');
  const [verificationOutcome, setVerificationOutcome] =
    React.useState<ServiceVerificationDecisionOutcome | null>(null);

  React.useEffect(() => {
    if (!specializationSuggestionId) {
      setOutcomeLoadState('idle');
      setSpecializationOutcome(null);
      return;
    }

    let cancelled = false;
    setOutcomeLoadState('loading');
    setSpecializationOutcome(null);

    void getOwnSpecializationProposalOutcome(specializationSuggestionId)
      .then(async (outcome) => {
        if (cancelled) return;
        setSpecializationOutcome(outcome);
        setOutcomeLoadState('success');
        if (notificationId) {
          try {
            await notificationService.markSpecializationOutcomeNotificationAsRead({
              notificationId,
              specializationId: specializationSuggestionId,
              outcome: outcome.outcome,
            });
          } catch (error) {
            console.error('Nie udało się potwierdzić odczytu powiadomienia specjalizacji:', error);
          }
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Nie udało się pobrać decyzji propozycji specjalizacji:', error);
        setOutcomeLoadState('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [notificationId, specializationSuggestionId]);

  React.useEffect(() => {
    if (!verificationDecisionId || !notificationId || !currentUser) {
      setVerificationOutcomeLoadState('idle');
      setVerificationOutcome(null);
      return;
    }

    let cancelled = false;
    setVerificationOutcomeLoadState('loading');
    setVerificationOutcome(null);

    void notificationService
      .getOwnServiceVerificationDecisionOutcome({
        notificationId,
        decisionId: verificationDecisionId,
      })
      .then(async (outcome) => {
        if (cancelled) return;
        setVerificationOutcome(outcome);
        setVerificationOutcomeLoadState('success');
        try {
          const marked =
            await notificationService.markServiceVerificationDecisionNotificationAsRead({
              notificationId,
              providerUserId: currentUser.uid,
              decisionId: verificationDecisionId,
              outcome: outcome.outcome,
            });
          if (!marked && !cancelled) {
            setVerificationOutcomeLoadState('unavailable');
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Nie udało się potwierdzić odczytu decyzji weryfikacyjnej:', error);
            setVerificationOutcomeLoadState('unavailable');
          }
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Nie udało się pobrać decyzji weryfikacyjnej:', error);
        setVerificationOutcomeLoadState('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser, notificationId, verificationDecisionId]);

  if (!currentUser) {
    return null;
  }

  return (
    <ServiceWorkspaceShell
      containerClassName="max-w-6xl"
      contentClassName="space-y-3 p-0 sm:space-y-3.5"
    >
      <ServiceWorkspaceBackLink onClick={() => navigate('/app/service')} />

      {verificationOutcomeLoadState === 'loading' && (
        <Alert variant="info" className="!px-3 !py-2 text-xs sm:text-sm">
          Sprawdzanie decyzji weryfikacyjnej…
        </Alert>
      )}

      {verificationOutcomeLoadState === 'success' && verificationOutcome && (
        <Alert
          variant={verificationOutcome.outcome === 'approved' ? 'success' : 'warning'}
          className="!px-3 !py-2 text-xs sm:text-sm"
        >
          <p className="font-semibold">
            {verificationOutcome.outcome === 'approved'
              ? 'Profil serwisu został zatwierdzony.'
              : 'Profil serwisu wymaga poprawek.'}
          </p>
          <p className="mt-1">
            Decyzja z {new Date(verificationOutcome.occurredAtMillis).toLocaleDateString('pl-PL')}.
          </p>
        </Alert>
      )}

      {verificationOutcomeLoadState === 'unavailable' && verificationDecisionId && (
        <Alert variant="warning" className="!px-3 !py-2 text-xs sm:text-sm">
          Nie udało się potwierdzić decyzji weryfikacyjnej.
          {notificationId ? ' Powiadomienie pozostaje nieprzeczytane.' : ''}
        </Alert>
      )}

      {outcomeLoadState === 'loading' && (
        <Alert variant="info" className="!px-3 !py-2 text-xs sm:text-sm">
          Sprawdzanie decyzji dotyczącej propozycji specjalizacji…
        </Alert>
      )}

      {outcomeLoadState === 'success' && specializationOutcome && (
        <Alert
          variant={specializationOutcome.outcome === 'accepted' ? 'success' : 'warning'}
          className="!px-3 !py-2 text-xs sm:text-sm"
        >
          <p className="font-semibold">Decyzja dotycząca propozycji specjalizacji</p>
          <p className="mt-1">
            {specializationOutcome.proposalLabel} · {specializationOutcome.proposalCategory}
          </p>
          <p className="mt-1">
            Wynik: {specializationOutcome.outcome === 'accepted' ? 'zaakceptowana' : 'odrzucona'} ·{' '}
            {new Date(specializationOutcome.decidedAtMillis).toLocaleDateString('pl-PL')}
          </p>
        </Alert>
      )}

      {outcomeLoadState === 'unavailable' && (
        <Alert variant="warning" className="!px-3 !py-2 text-xs sm:text-sm">
          Nie udało się potwierdzić decyzji dotyczącej tej propozycji.
          {notificationId ? ' Powiadomienie pozostaje nieprzeczytane.' : ''}
        </Alert>
      )}

      {isOnboardingFlow ? (
        <Alert variant="warning" className="!px-3 !py-2 text-xs sm:text-sm">
          Aby odblokować zlecenia serwisu, uzupełnij dane i wyślij profil do weryfikacji
          administratora.
        </Alert>
      ) : null}
      <ServiceProviderProfileForm
        mode={isOnboardingFlow ? 'onboarding' : 'default'}
        forceResubmissionHint={forceResubmission}
        onSubmittedForVerification={(payload) => {
          navigate('/app/service', {
            replace: true,
            state: {
              onboardingBootstrapStatus: payload?.verificationStatus || 'pending_approval',
            },
          });
        }}
      />
    </ServiceWorkspaceShell>
  );
};

export default EditServiceProfilePage;
